// apiHelpers.js - Utility functions for API calls
import { apiClient, getApiUrl } from "../shared/api/AllApi";
import handleApiError from "./errorHandler";

/**
 * Make a GET request using the centralized API client
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} config - Axios request config
 * @returns {Promise} - Axios promise
 */
export const apiGet = (endpoint, config = {}) => {
    return apiClient.get(endpoint, config);
};

/**
 * Make a POST request using the centralized API client
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} data - Data to send in the request body
 * @param {object} config - Axios request config
 * @returns {Promise} - Axios promise
 */
export const apiPost = (endpoint, data = {}, config = {}) => {
    return apiClient.post(endpoint, data, config);
};

/**
 * Make a PUT request using the centralized API client
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} data - Data to send in the request body
 * @param {object} config - Axios request config
 * @returns {Promise} - Axios promise
 */
export const apiPut = (endpoint, data = {}, config = {}) => {
    return apiClient.put(endpoint, data, config);
};

/**
 * Make a DELETE request using the centralized API client
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} config - Axios request config
 * @returns {Promise} - Axios promise
 */
export const apiDelete = (endpoint, config = {}) => {
    return apiClient.delete(endpoint, config);
};

/**
 * Make a request using fetch API with the correct base URL
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
export const apiFetch = (endpoint, options = {}) => {
    return fetch(getApiUrl(endpoint), options);
};

/**
 * Enhanced GET request with built-in error handling
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} config - Axios request config
 * @param {Function} t - Translation function from react-i18next
 * @param {object} errorOptions - Options for error handling
 * @returns {Promise} - Promise that resolves to response data or rejects with error
 */
export const safeApiGet = async (endpoint, config = {}, t = null, errorOptions = {}) => {
    try {
        const response = await apiClient.get(endpoint, config);
        return response.data;
    } catch (error) {
        handleApiError(error, t, errorOptions);
        throw error;
    }
};

/**
 * Enhanced POST request with built-in error handling
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} data - Data to send in the request body
 * @param {object} config - Axios request config
 * @param {Function} t - Translation function from react-i18next
 * @param {object} errorOptions - Options for error handling
 * @returns {Promise} - Promise that resolves to response data or rejects with error
 */
export const safeApiPost = async (endpoint, data = {}, config = {}, t = null, errorOptions = {}) => {
    try {
        const response = await apiClient.post(endpoint, data, config);
        return response.data;
    } catch (error) {
        handleApiError(error, t, errorOptions);
        throw error;
    }
};

/**
 * Enhanced PUT request with built-in error handling
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} data - Data to send in the request body
 * @param {object} config - Axios request config
 * @param {Function} t - Translation function from react-i18next
 * @param {object} errorOptions - Options for error handling
 * @returns {Promise} - Promise that resolves to response data or rejects with error
 */
export const safeApiPut = async (endpoint, data = {}, config = {}, t = null, errorOptions = {}) => {
    try {
        const response = await apiClient.put(endpoint, data, config);
        return response.data;
    } catch (error) {
        handleApiError(error, t, errorOptions);
        throw error;
    }
};

/**
 * Enhanced DELETE request with built-in error handling
 * @param {string} endpoint - API endpoint without the base URL
 * @param {object} config - Axios request config
 * @param {Function} t - Translation function from react-i18next
 * @param {object} errorOptions - Options for error handling
 * @returns {Promise} - Promise that resolves to response data or rejects with error
 */
export const safeApiDelete = async (endpoint, config = {}, t = null, errorOptions = {}) => {
    try {
        const response = await apiClient.delete(endpoint, config);
        return response.data;
    } catch (error) {
        handleApiError(error, t, errorOptions);
        throw error;
    }
};

